import { useSortable } from "@dnd-kit/sortable";
import { useDndContext } from "@dnd-kit/core";
import React, { memo } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import classes from "../../DragNDropSortableList.scss";
import { CSS } from "@dnd-kit/utilities";

/**
 * ListItem - A sortable list item component
 *
 * This component wraps each item in the sortable list and provides
 * the necessary drag functionality through @dnd-kit's useSortable hook.
 *
 * @component
 * @example
 * <DragNDropSortableList.ListItem
 *   id="item-1"
 *   data={{ name: 'Item 1' }}
 * >
 *   <div>Item Content</div>
 * </DragNDropSortableList.ListItem>
 */
const ListItem = ({
  id,
  children,
  className,
  isDragOverlay,
  tabIndex,
  data,
  isDragOverlayUsed,
}) => {
  const {
    attributes,
    setNodeRef,
    transform,
    transition,
    isDragging,
    activeIndex,
    overIndex,
  } = useSortable({
    id,
    data,
  });
  const { active, over } = useDndContext();

  const containerStyles = {
    transform: CSS.Translate.toString(transform),
    transition,
  };

  const isActiveDragState =
    !isDragOverlay &&
    Boolean(active && over) &&
    active.id !== over.id &&
    Number.isInteger(activeIndex) &&
    Number.isInteger(overIndex) &&
    activeIndex >= 0 &&
    overIndex >= 0;
  const isDraggingOver = isActiveDragState && !isDragging && over.id === id;
  const isDraggingDown = isActiveDragState && activeIndex < overIndex;
  const dropIndicator = isDraggingOver
    ? isDraggingDown
      ? "bottom"
      : "top"
    : undefined;

  return (
    <div
      className={classNames(classes.listItem, className, {
        [classes.dragOverlay]: isDragOverlay,
        [classes.dragging]: isDragOverlayUsed && isDragging && !isDragOverlay,
      })}
      style={containerStyles}
      ref={setNodeRef}
      data-dragging={isDragging}
      data-drop-indicator={dropIndicator}
      {...attributes}
      tabIndex={tabIndex}
    >
      {children}
    </div>
  );
};

ListItem.propTypes = {
  /** Unique identifier for the list item */
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  /** Child components to render within the list item */
  children: PropTypes.node.isRequired,
  /** Additional CSS class name for custom styling */
  className: PropTypes.string,
  /** Flag to indicate if this is a drag overlay element */
  isDragOverlay: PropTypes.bool,
  /** Tab index for keyboard navigation */
  tabIndex: PropTypes.number,
  /** Additional data to associate with the sortable item */
  data: PropTypes.any,
  /** Flag to determine if drag overlay styling should be used */
  isDragOverlayUsed: PropTypes.bool,
};

ListItem.defaultProps = {
  className: "",
  isDragOverlay: false,
  tabIndex: -1,
  data: undefined,
  isDragOverlayUsed: true,
};

export default memo(ListItem);
